// Client layer (TS-2.5.03). The dual-run comparison screen ingestion.ts exposes
// (ingestionApi.compareSources) - independent of the upload wizard above it, since comparing two
// already-configured sources needs no file upload at all.
import { useState } from "react";
import { ApiError } from "../../api/ApiError";
import type { SourceComparison } from "../../api/generated/contracts";
import { ingestionApi } from "../../api/ingestion";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { strings } from "../../strings";

const S = strings.import.compare;

export function ImportCompareForm() {
  const [baseline, setBaseline] = useState("");
  const [comparand, setComparand] = useState("");
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<SourceComparison | null>(null);

  async function handleSubmit() {
    setComparing(true);
    setError(null);
    try {
      const next = await ingestionApi.compareSources({
        baselineProfileCode: baseline.trim(),
        comparandProfileCode: comparand.trim(),
        ignoredFieldNames: [],
        maxProjects: null,
      });
      setResult(next);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The comparison could not be run."));
    } finally {
      setComparing(false);
    }
  }

  return (
    <section className="eg-import-compare" aria-labelledby="eg-import-compare-heading">
      <h2 id="eg-import-compare-heading">{S.heading}</h2>

      <FormField id="eg-import-compare-baseline" label={S.baseline} required>
        <input
          type="text"
          required
          value={baseline}
          onChange={(event) => setBaseline(event.target.value)}
          {...formFieldControlProps("eg-import-compare-baseline")}
        />
      </FormField>

      <FormField id="eg-import-compare-comparand" label={S.comparand} required>
        <input
          type="text"
          required
          value={comparand}
          onChange={(event) => setComparand(event.target.value)}
          {...formFieldControlProps("eg-import-compare-comparand")}
        />
      </FormField>

      <button type="button" disabled={comparing || !baseline.trim() || !comparand.trim()} onClick={() => void handleSubmit()}>
        {comparing ? S.comparing : S.submit}
      </button>

      {error && (
        <div role="alert">
          <p>{S.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {result && (
        <dl>
          <dt>{S.onlyInBaseline}</dt>
          <dd>{result.onlyInBaseline.length}</dd>
          <dt>{S.onlyInComparand}</dt>
          <dd>{result.onlyInComparand.length}</dd>
          <dt>{S.differing}</dt>
          <dd>{result.differing.length}</dd>
          <dt>{S.identicalCount}</dt>
          <dd>{result.identicalProjectCount}</dd>
        </dl>
      )}
    </section>
  );
}
